import React from 'react';
import { Box, Typography, IconButton, Paper, Collapse, Tooltip, Alert } from '@mui/material';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';

import { neutral } from '_lex/_foundations/phxColors';
import { TemplateSection, TemplateField, DragData, Template } from '../../_types';
import { isSectionLockedByParent } from '../../_utils/sectionComparison';
import { translate as t } from 'hooks/useTranslations';
import { useTemplateBuilderContext } from '../context';

import SortableFieldItem from './SortableFieldItem';
import SectionDropZone from './SectionDropZone';
import InsertableDropZone from './InsertableDropZone';

interface SortableSectionProps {
  section: TemplateSection;
  sections: TemplateSection[];
  parentTemplate?: Template | null;
  onRemoveField: (sectionId: number, fieldId: number) => void;
  onToggleCollapsed: (sectionId: number) => void;
  isReorderMode: boolean;
  onRemoveSection: (sectionId: number) => Promise<void>;
  canDelete: boolean; // Only allow delete when multiple sections exist
  onSetSelectedField: (field: TemplateField) => void;
  onSetSelectedSection: (section: TemplateSection) => void;
  activeId?: string | null; // Add activeId to show drop zones
  dragData?: DragData | null; // Add dragData to determine drag type
  openFieldOptionsId: string | null;
  setOpenFieldOptionsId: (fieldId: string | null) => void;
  inactiveFieldIds?: Set<number>;
  duplicateFieldIds?: Set<number>;
  duplicateSectionIds?: Set<number>;
  emptySectionIds?: Set<number>;
  removableChildFieldIds?: Set<number>;
  selectedSectionId?: number | null;
  selectedSectionGuid?: string | null;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  sections,
  parentTemplate,
  onRemoveField,
  onToggleCollapsed,
  isReorderMode,
  onRemoveSection,
  canDelete,
  onSetSelectedField,
  onSetSelectedSection,
  activeId,
  dragData: currentDragData,
  openFieldOptionsId,
  setOpenFieldOptionsId,
  inactiveFieldIds = new Set(),
  duplicateFieldIds = new Set(),
  duplicateSectionIds = new Set(),
  emptySectionIds = new Set(),
  removableChildFieldIds = new Set(),
  selectedSectionId,
  selectedSectionGuid,
}) => {
  const dragData: DragData = {
    type: 'section',
    section,
  };

  const { isLexipolTemplate } = useTemplateBuilderContext();
  const templateMode = isLexipolTemplate ? 'lexipol' : 'agency';

  // Check if section is locked by parent
  const isSectionLocked = isSectionLockedByParent(section, parentTemplate);

  // Check if this section is selected in the properties panel
  // Use sectionGuid for comparison when section has id: 0, otherwise use id
  const isSelected = section.id !== 0 ? selectedSectionId === section.id : selectedSectionGuid === section.sectionGuid;

  // Check if this section has a duplicate name
  const isDuplicate = duplicateSectionIds.has(section.id);

  // Check if this section is empty (has no active fields)
  const isEmpty = emptySectionIds.has(section.id);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id === 0 ? `temp-section-${section.sectionGuid}` : String(section.id), // Use hybrid ID for uniqueness
    data: dragData,
    disabled: !isReorderMode,
  });

  const style: React.CSSProperties = isDragging
    ? {
        opacity: 0,
        pointerEvents: 'none',
      }
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };

  // Filter out deleted fields and use hybrid ID strategy: fieldId for new fields (id: 0), otherwise use id
  const visibleFields = (section.sectionFields || []).filter((f) => !f.isDeleted && !f.effectiveIsDeleted);
  const fieldIds = visibleFields.map((f) => (f.id === 0 ? `temp-field-${f.fieldId}` : String(f.id)));

  const handleToggleCollapsed = () => {
    onToggleCollapsed(section.id);
  };

  const handleRemoveSection = async () => {
    try {
      await onRemoveSection(section.id);
    } catch (error) {
      console.error('Error removing section:', error);
    }
  };

  const handleSectionSettings = () => {
    onSetSelectedSection(section);
  };

  const showManualDropZones =
    activeId !== null &&
    !isReorderMode &&
    (currentDragData?.type === 'field-from-bank' ||
      (currentDragData?.type === 'field-within-section' && currentDragData.sourceSectionId !== section.id));

  return (
    <Box
      sx={{
        border: '2px solid',
        borderColor: isDuplicate
          ? 'error.main'
          : isEmpty
            ? 'warning.main'
            : isSelected
              ? 'primary.main'
              : 'transparent',
        borderRadius: 2,
        transition: 'border-color 0.2s ease',
      }}
    >
      {/* Yellow banner for locked sections - shown above the section (hidden in reorder mode) */}
      {isSectionLocked && !isReorderMode && (
        <Alert
          severity='warning'
          icon={<LockIcon fontSize='small' />}
          sx={{
            borderRadius: '8px 8px 0 0', // Round top corners only
            '& .MuiAlert-icon': {
              color: '#ed6c02',
            },
            '& .MuiAlert-message': {
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            },
          }}
        >
          {t(`performanceReporting.templates.builder.${templateMode}.lockedSection.banner`)}
        </Alert>
      )}

      <Paper
        ref={setNodeRef}
        style={style}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: neutral[200],
          borderRadius: isSectionLocked && !isReorderMode ? '0 0 8px 8px' : 2, // Round bottom corners only if banner present
          backgroundColor: 'background.paper',
          overflow: 'visible', // Allow tooltips to be visible
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 4,
            backgroundColor: neutral[50],
            borderBottom: '1px solid',
            borderBottomColor: neutral[200],
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {/* Expand/Collapse button */}
            <IconButton
              size='small'
              onClick={handleToggleCollapsed}
              sx={{ color: neutral[600] }}
            >
              {section.collapsed ? <ExpandMoreIcon fontSize='small' /> : <ExpandLessIcon fontSize='small' />}
            </IconButton>

            {/* Section title */}
            <Typography
              variant='subtitle1'
              fontWeight='bold'
              sx={{
                flex: 1,
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
              onClick={handleSectionSettings}
            >
              {section.effectiveName}
            </Typography>
          </Box>

          {/* Action buttons and reorder handle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Settings button - always visible */}
            <Tooltip
              title='Section settings'
              placement='top'
            >
              <IconButton
                size='small'
                onClick={handleSectionSettings}
                sx={{
                  color: neutral[400],
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                <SettingsIcon fontSize='small' />
              </IconButton>
            </Tooltip>

            {/* Delete button - only show if multiple sections exist */}
            {canDelete && (
              <Tooltip
                title='Delete section'
                placement='top'
              >
                <IconButton
                  size='small'
                  onClick={handleRemoveSection}
                  sx={{
                    color: neutral[400],
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    },
                  }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}

            {/* Reorder handle - only show in reorder mode */}
            {isReorderMode && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: neutral[400],
                  cursor: 'grab',
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: neutral[100],
                    color: neutral[600],
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon fontSize='small' />
              </Box>
            )}
          </Box>
        </Box>

        {/* Section Content */}
        <Collapse in={!section.collapsed}>
          <Box sx={{ p: 3 }}>
            {/* Section Description */}
            {section.effectiveDescription && (
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  px: 2,
                  py: 4,
                }}
              >
                {section.effectiveDescription}
              </Typography>
            )}

            {visibleFields.length === 0 ? (
              <SectionDropZone
                sectionId={section.id}
                sectionGuid={section.sectionGuid}
                isEmpty={true}
                isLocked={isSectionLocked}
              />
            ) : (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <SortableContext
                  items={fieldIds}
                  strategy={verticalListSortingStrategy}
                >
                  {/* Insert drop zone at the beginning */}
                  <InsertableDropZone
                    sectionId={section.id}
                    sectionGuid={section.sectionGuid}
                    insertIndex={0}
                    isVisible={showManualDropZones}
                    dragData={currentDragData}
                    sectionFields={section.sectionFields}
                    isLocked={isSectionLocked}
                    sections={sections}
                    parentTemplate={parentTemplate}
                  />

                  {visibleFields.map((field, index) => (
                    <React.Fragment key={field.id === 0 ? `temp-field-${field.fieldId}-${index}` : field.id}>
                      <SortableFieldItem
                        field={field}
                        sectionId={section.id}
                        section={section}
                        parentTemplate={parentTemplate}
                        onRemove={onRemoveField}
                        onSetSelectedField={onSetSelectedField}
                        openFieldOptionsId={openFieldOptionsId}
                        setOpenFieldOptionsId={setOpenFieldOptionsId}
                        isInactive={inactiveFieldIds.has(field.id)}
                        isDuplicate={duplicateFieldIds.has(field.id)}
                        isRemovableChildField={removableChildFieldIds.has(field.id)}
                      />
                      {/* Insert drop zone after each field */}
                      <InsertableDropZone
                        sectionId={section.id}
                        sectionGuid={section.sectionGuid}
                        insertIndex={index + 1}
                        isVisible={showManualDropZones}
                        dragData={currentDragData}
                        sectionFields={section.sectionFields}
                        isLocked={isSectionLocked}
                        sections={sections}
                        parentTemplate={parentTemplate}
                      />
                    </React.Fragment>
                  ))}
                </SortableContext>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default SortableSection;
